import Select, { SingleValue } from 'react-select';
import React, { useState } from 'react';

import SkillsSelectorV1 from './SkillsSelectorV1';
import { generateReactSelectOptionsFromObject } from 'utils/reactSelect';

import type { QueryObject } from 'interface/queryParam';

import type { DesignationRecord, FlatSelectOption } from '../types';

export interface ResourceFilterV1Props {
  loadingSkills: boolean;
  skills: DesignationRecord[];
  loadingDesignations: boolean;
  designations: DesignationRecord[];
  initialState: QueryObject;
  onFilter: (payload: QueryObject) => void;
  onClear?: () => void;
}

interface ResourceFilterV1LocalState {
  skill: Array<number | string | null>;
  rating: Array<number | string | null>;
  designationId: string | number | null;
  rowIds: string[];
}

let nextRowId = 0;
function generateRowId(): string {
  return `skill-row-${nextRowId++}`;
}

function scalarFromQuery(value: QueryObject[string]): string | number | null {
  if (value === undefined || value === null) {
    return null;
  }
  if (typeof value === 'boolean') {
    return null;
  }
  if (Array.isArray(value)) {
    const first = value[0];

    if (first === undefined || first === null || typeof first === 'boolean') {
      return null;
    }

    return first;
  }

  return value;
}

function buildInitialFilterState(initialState: QueryObject): ResourceFilterV1LocalState {
  const initialFilterState = {
    ...initialState,
    designationId: scalarFromQuery(initialState.designationId),
    rowIds: [],
  } as ResourceFilterV1LocalState;

  if (!initialFilterState.skill || !initialFilterState.skill.length) {
    initialFilterState.skill = [null];
    initialFilterState.rating = [0];
  }

  initialFilterState.rowIds = Array.from({ length: initialFilterState.skill.length }, () => generateRowId());

  return initialFilterState;
}

const ResourceFilterV1: React.FC<ResourceFilterV1Props> = ({
  loadingSkills,
  skills,
  loadingDesignations,
  designations,
  initialState,
  onFilter,
  onClear,
}) => {
  const [filterState, setFilterState] = useState<ResourceFilterV1LocalState>(() =>
    buildInitialFilterState(initialState)
  );
  const [error, setError] = useState('');

  const handleChange = (name: 'designationId', value: string | number | null) => {
    setFilterState((prev) => ({ ...prev, [name]: value }));

    setError('');
  };

  const handleSkillChange = (index: number, value: number | string | undefined) => {
    setFilterState((prev) => {
      const newFilterState = { ...prev };

      if (newFilterState.skill.length >= index + 1) {
        newFilterState.skill[index] = value ?? null;
      }

      return newFilterState;
    });
    setError('');
  };

  const handleRatingChange = (index: number, value: number | null) => {
    setFilterState((prev) => {
      const newFilterState = { ...prev };

      if (newFilterState.rating.length >= index + 1) {
        newFilterState.rating[index] = value ?? 0;
      }

      return newFilterState;
    });
    setError('');
  };

  const handleSkillAddition = () => {
    setFilterState((prev) => {
      const newFilterState = { ...prev };

      newFilterState.skill.push(null);
      newFilterState.rating.push(0);
      newFilterState.rowIds.push(generateRowId());

      return newFilterState;
    });
    setError('');
  };

  const handleClear = () => {
    setFilterState({
      skill: [null],
      rating: [0],
      designationId: null,
      rowIds: [generateRowId()],
    });

    setError('');

    if (onClear) {
      onClear();
    }
  };

  const handleFilter = () => {
    let valid = true;
    const { skill, rating: originalRating, designationId } = filterState;
    const normalizedSkill = [...skill];
    const normalizedRating = skill.map((_, index) => {
      const ratingValue = originalRating[index];
      const numericRating = Number(ratingValue ?? 0);
      return Number.isFinite(numericRating) ? numericRating : null;
    });

    normalizedSkill.forEach((skillItem, index) => {
      if (!skillItem && normalizedRating[index]) {
        setError(`Skill missing on row: ${index + 1}`);
        valid = false;
      } else if (!skillItem && !normalizedRating[index]) {
        normalizedRating[index] = null;
      }
    });

    if (valid) {
      let skillFiltered: Array<number | string> | null = normalizedSkill.filter((s) => s);
      let ratingFiltered: Array<number | string> | null = normalizedRating.filter((r) => r != null);

      if (!skillFiltered || !skillFiltered.length) {
        skillFiltered = null;
        ratingFiltered = null;
      }

      onFilter({ skill: skillFiltered, rating: ratingFiltered, designationId });
    }
  };

  const skillOptions = generateReactSelectOptionsFromObject(skills);
  const designationOptions = generateReactSelectOptionsFromObject(designations);

  return (
    <>
      <div className="table-header name-wrap d-flex flex-row filter-header">
        <span className="resource-header-title-v1">{'Filter By'}</span>
      </div>
      <div className="skills-selector">
        <SkillsSelectorV1
          loading={loadingSkills}
          skillOptions={skillOptions}
          skills={filterState.skill}
          ratings={filterState.rating}
          rowIds={filterState.rowIds}
          onSkillChange={handleSkillChange}
          onRatingChange={handleRatingChange}
          onAddSkill={handleSkillAddition}
        />
        {error && <span className="color-red">{error}</span>}
      </div>
      <div className="designation-selector">
        <span className="filter-label">{'Role'}</span>
        <Select<FlatSelectOption>
          value={designationOptions.find((option) => option.value === +String(filterState.designationId ?? '')) ?? null}
          isSearchable={true}
          isClearable={true}
          isLoading={loadingDesignations}
          options={designationOptions}
          onChange={(option: SingleValue<FlatSelectOption>) => {
            handleChange('designationId', option?.value ?? null);
          }}
        />
      </div>
      <div className="filter-control d-flex flex-row">
        <button type="button" className="btn btn--primary" onClick={handleFilter}>
          {'Apply Filter'}
        </button>
        <button type="button" className="btn btn--outlined-grey ml-10" onClick={handleClear}>
          {'Clear'}
        </button>
      </div>
    </>
  );
};

export default ResourceFilterV1;
